import { Helmet } from 'react-helmet-async'
import { useLocation } from 'react-router-dom'

import { faArrowDown, faArrowUp, faExchange } from '@fortawesome/pro-light-svg-icons'

import {
  Box, Columns, DashboardModule, LoadingModule,
} from '@domain-group/suit-theme'

import { getQueryParamsFromHash, parsePermittedQueryParams } from '@domain-group/javascript-utils'

import useAgencySelector from '@hooks/useAgencySelector'
import useCurrentUser from '@hooks/useCurrentUser'
import { generateUrls } from '@hooks/useCampaignPortalSync'
import useCampaignPortalSyncs from '@hooks/useCampaignPortalSyncs'

import NotificationBannerLocation from '@components/NotificationBannerLocation'
import NotificationModuleLocation from '@components/NotificationModuleLocation'
import PageHeader from '@components/PageHeader'

import PortalSyncsBlankState from '../PortalSyncsBlankState'
import PortalSync from '../PortalSyncListItem'

const PageContent = () => {
  const { agencyIdParam, selectedAgencyId } = useAgencySelector()
  const { isAgent, isEbuAdmin } = useCurrentUser()

  useLocation()

  // @ts-expect-error TS2339
  const { sync_type }: { redirect?: string } = parsePermittedQueryParams(getQueryParamsFromHash(), ['sync_type'])

  const campaignPortalSyncsPayload = useCampaignPortalSyncs({
    filters: {
      agency_id: isAgent || isEbuAdmin ? undefined : selectedAgencyId,
      sync_type,
    },
    performHttpRequests: true,
    requestOptions: {
      include_campaign_portal_sync_campaign: true,
      include_campaign_portal_sync_portal: true,
    },
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filteredCampaignPortalSyncs,
    filteredCampaignPortalSyncsCount,
    hasFilteredCampaignPortalSyncs,
    loading,
  } = campaignPortalSyncsPayload

  const {
    campaignPortalSyncsOverview,
    campaignPortalSyncsExportOverview,
    campaignPortalSyncsImportOverview,
  } = generateUrls({ prefixRoute: `agency/dashboard/agencies/${agencyIdParam}` })

  return (
    <>
      <Helmet>
        <title>Portal Syncs | Realhub</title>
      </Helmet>

      <PageHeader
        nestedNavigation
        activeTabBarItemKey={sync_type || 'overview'}
        tabBarItems={[
          {
            href: campaignPortalSyncsOverview,
            icon: faExchange,
            key: 'overview',
            title: 'All Syncs',
          },
          {
            href: campaignPortalSyncsImportOverview,
            icon: faArrowDown,
            key: 'import',
            title: 'Imports',
          },
          {
            href: campaignPortalSyncsExportOverview,
            icon: faArrowUp,
            key: 'export',
            title: 'Exports',
          },
        ]}
        title="Portal Syncs"
      />

      <Box paddingX="large" paddingTop={[112, 105]}>
        <Columns boxProps={{ marginTop: 'large' }} flexDirection={['column', 'column', 'row']}>
          <Columns.Main>
            <NotificationBannerLocation
              locationKey="agency.agencyDashboard.portalSyncs.banner"
            />

            <LoadingModule loading={loading} times={3} />

            {!loading && !hasFilteredCampaignPortalSyncs && <PortalSyncsBlankState />}

            {!loading && hasFilteredCampaignPortalSyncs && (
              <DashboardModule
                title="Sync History"
                contentBoxProps={{ flexDirection: 'column' }}
              >
                {/*
                 // @ts-expect-error TS2322 */}
                <DashboardModule.ScrollableSection maxHeight={600}>
                  {filteredCampaignPortalSyncs.map(campaignPortalSync => (
                    <PortalSync
                      campaignPortalSync={campaignPortalSync}
                      key={campaignPortalSync.id}
                    />
                  ))}
                </DashboardModule.ScrollableSection>

                <DashboardModule.LoadMoreFooter
                  boxProps={{ borderTop: loading ? 'none' : '1px solid' }}
                  callbacks={{ loadMore }}
                  canLoadMore={canLoadMore}
                  entityCount={filteredCampaignPortalSyncsCount}
                  loading={loading}
                />
              </DashboardModule>
            )}
          </Columns.Main>

          <Columns.Sidebar>
            <NotificationModuleLocation
              locationKey="agency.agencyDashboard.portalSyncs.sidebar"
            />
          </Columns.Sidebar>
        </Columns>
      </Box>
    </>
  )
}

export default PageContent
